import React, { useRef, useCallback, useMemo, useEffect, useState, useContext, Fragment } from "react";

import COMMON from "common";
import api from "services/api";
import serveRequestErrors from "common/serve-request-errors";
import AppEmptyState from "components/app-empty-state";
import AppStatus from "components/app-status";
import AppTable, { AppTableCell, AppTableEditIcon } from "components/app-table";
import AppSchedulerModal from "components/pages/settings/app-scheduler-modal";
import { AxiosContext } from "contexts/with-interceptor-provider";
import { parseCronToBuilder, describeCronSchedule, getNextRunDate } from "common/cron-schedule";
import { formatDateTime } from "common/calendar";

const PageScheduler = () => {
	const schedulerModalRef = useRef();
	const context = useContext(AxiosContext);
	const [items, setItems] = useState([]);
	const [loading, setLoading] = useState(true);
	const isEmptyState = useMemo(() => !loading && items.length === 0, [loading, items]);
	const pages = useMemo(() => ({ page: 1, size: items.length || 10, total: 1, offset: 0, totalElements: items.length, prev: false, next: false, items }), [items]);

	const onHandleGetList = useCallback(async () => {
		let response = null;

		try {
			response = await api.get.scheduler.list();
		} catch (error) {
			serveRequestErrors(error);
		}

		if (response) {
			setItems(response);
		}

		setLoading(false);
	}, []);

	// TODO: hide/disable the edit action for users without hr:setup:scheduler:update once the app has a permission-string gating mechanism (none exists yet, see auth-routes.js role-name checks)
	const onHandleEdit = useCallback((job) => {
		schedulerModalRef.current.onHandleShow(job);
	}, []);

	const onHandleUpdated = useCallback((updatedJob) => {
		setItems((prev) => prev.map((job) => (job.jobKey === updatedJob.jobKey ? updatedJob : job)));
	}, []);

	//prettier-ignore
	const NameCell = useCallback(({ row }) => {
        return (
            <div className="scheduler__name-cell">
                <p className="scheduler__name-cell-title">{row.original.jobName}</p>
                {row.original.description && <p className="scheduler__name-cell-description">{row.original.description}</p>}
            </div>
        );
    }, []);

	//prettier-ignore
	const StatusCell = useCallback(({ row }) => {
        const status = row.original?.enabled ? COMMON.STATUS_ID.ENABLED : COMMON.STATUS_ID.DISABLED;
        return <AppStatus status={status} />;
    }, []);

	//prettier-ignore
	const ScheduleCell = useCallback(({ row }) => {
        const parsed = parseCronToBuilder(row.original.cronExpression);
        const value = parsed.unsupported ? row.original.cronExpression : describeCronSchedule(parsed);
        return <AppTableCell left value={value || "-"} />;
    }, []);

	//prettier-ignore
	const NextRunCell = useCallback(({ row }) => {
        const nextRun = getNextRunDate(row.original.cronExpression, row.original.enabled);
        return <AppTableCell left value={nextRun ? formatDateTime(nextRun) : "Not scheduled"} />;
    }, []);

	//prettier-ignore
	const ActionCell = useCallback(({ row }) => {
        return <AppTableEditIcon onClick={() => onHandleEdit(row.original)} />;
    }, [onHandleEdit]);

	//prettier-ignore
	const columns = useMemo(() => [
		{
			Header: "Scheduler",
			accessor: "jobName",
			disableSortBy: true,
			Cell: NameCell,
		},
		{
			Header: "Status",
			accessor: "enabled",
			disableSortBy: true,
			Cell: StatusCell,
		},
		{
			Header: "Schedule",
			accessor: "cronExpression",
			disableSortBy: true,
			Cell: ScheduleCell,
		},
		{
			Header: "Next run",
			accessor: "nextRun",
			disableSortBy: true,
			Cell: NextRunCell,
		},
		{
			Header: "",
			accessor: "*",
			disableSortBy: true,
			Cell: ActionCell,
		},
	], [NameCell, StatusCell, ScheduleCell, NextRunCell, ActionCell]);

	useEffect(() => {
		onHandleGetList();
	}, [onHandleGetList]);

	useEffect(() => {
		return () => {
			context.onHandleCancelRequest(COMMON.ENDPOINT_PATH.SCHEDULER.LIST);
		};
	}, [context]);

	return (
		<div className="page-scheduler">
			<div className="scheduler">
				{isEmptyState && <AppEmptyState title="No schedulers found" description="You don't have any schedulers yet" hideButton />}

				{!isEmptyState && (
					<Fragment>
						<AppTable columns={columns} pages={pages} />
					</Fragment>
				)}
			</div>

			<AppSchedulerModal ref={schedulerModalRef} onHandleUpdated={onHandleUpdated} />
		</div>
	);
};

export default PageScheduler;
